import {BannerIcon, UserIcon} from '@jobseeker/lotus/assets/icons';
import HiristBannerComponent from '@jobseeker/lotus/components/HiristBanner';
import {Typography, Button, Skeleton, Card} from '@mui/material';
import {Box, Grid} from '@jobseeker/lotus/foundations';
import {PendingTestsTitle} from '@jobseeker/lotus/components/index';
import {useTheme} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {pendingAssessmentService} from '@jobseeker/common-core/services';
import Link from 'next/link';
import {Image} from '@jobseeker/lotus/components/Image';

interface PendingTest {
  id: number;
  jobId: number;
  testId: string;
  jobTitle: string;
  createdOn: string;
  recruiterName: string;
  designation: string;
  profileUrl: string;
  companyName: string;
  logoPath: string;
  testName: string;
  recruiterId: number;
}

const PendingAssessments = () => {
  const theme = useTheme();
  const MdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [tests, setTests] = useState<PendingTest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const {
    userId,
    authInfo: {token},
  } = useSelector((state: AppState) => state.auth);
  const isHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

  useEffect(() => {
    const fetchTests = async () => {
      setIsLoading(true);
      try {
        const data = await pendingAssessmentService.getPendingAssessments(
          userId
        );
        setTests(data?.data || []);
      } catch (err) {
        console.error('Error fetching pending tests:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchTests();
    }
  }, [userId]);

  const LoadingSkeleton = () => (
    <Box
      sx={{
        backgroundColor: 'transparent',
        px: {xs: 1, md: 4, lg: 4, xl: 6},
        pt: {xs: 1.5, md: 4, lg: 4},
        pb: {xs: 1.5, md: 5, lg: 5, xl: 6},
      }}
    >
      <Skeleton variant="rounded" width={'30%'} height={32} sx={{mb: 2}} />
      {MdUp && (
        <Grid
          container
          mb={2}
          p={2}
          borderRadius="8px"
          bgcolor="white"
          height={50}
          alignItems="center"
        >
          <Grid item md={3}>
            <Skeleton variant="rounded" width={'60%'} height={24} />
          </Grid>
          <Grid item md={7}>
            <Skeleton variant="rounded" width={'40%'} height={24} />
          </Grid>
          <Grid item md={2}>
            <Skeleton variant="rounded" width={'80%'} height={24} />
          </Grid>
        </Grid>
      )}

      {[...Array(3)].map((_, index) => (
        <Grid
          container
          key={`skeleton-${index}`}
          px={2}
          py={1.5}
          mb={2.5}
          borderRadius="8px"
          bgcolor="white"
          alignItems="center"
        >
          <Grid item xs={12} sm={9} md={3}>
            <Skeleton variant="rounded" width={'80%'} height={24} />
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
            pt={{xs: 1.9, md: 0}}
            mt={{sm: 1.6, md: 0}}
            borderTop={{xs: 1, md: 0}}
            borderColor={{xs: '#efefef'}}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              <Skeleton variant="rounded" width={'90%'} height={24} />
              <Box display="flex" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box flex={1}>
                  <Skeleton variant="rounded" width={'60%'} height={20} />
                  <Skeleton
                    variant="rounded"
                    width={'40%'}
                    height={20}
                    sx={{mt: 0.5}}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={3}
            md={2}
            display="flex"
            justifyContent="flex-end"
          >
            <Skeleton variant="rounded" width={'100%'} height={36} />
          </Grid>
        </Grid>
      ))}
    </Box>
  );

  return (
    <>
      {isHirist && (
        <HiristBannerComponent>
          <Box display="flex" alignItems="center">
            <Box width={88} mr={4}>
              <Image
                src={BannerIcon}
                alt="banner-icon"
                width={70}
                height={70}
              />
            </Box>
            <Box>
              <Typography
                color="white"
                fontSize={16}
                display={{xs: 'none', md: 'block'}}
              >
                Pending Tests
              </Typography>
              <Typography
                color="white"
                fontSize={{sx: 16, md: 22}}
                fontWeight={700}
              >
                Complete your pending test(s) from here.
              </Typography>
            </Box>
          </Box>
        </HiristBannerComponent>
      )}

      {isLoading ? (
        <LoadingSkeleton />
      ) : tests.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mt: {xs: 0, sm: '50px'},
            px: '30px',
            py: '40px',
          }}
        >
          <Typography
            sx={{
              fontSize: {xs: '20px', md: '24px'},
              fontWeight: 600,
              my: {xs: '20px', md: '24px'},
              textAlign: 'center',
            }}
          >
            You Don't Have Pending Tests
          </Typography>

          <Box
            sx={{
              width: {xs: '230px', md: '450px'},
              height: {xs: '200px', md: '400px'},
              position: 'relative',
            }}
          >
            <Image
              src="https://job-static.hirist.com/V8/static/media/no-result-illustration.d2c11a15.svg"
              alt="No pending test icon"
              fill
              style={{objectFit: 'contain'}}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'transparent',
            px: {xs: 1, md: 4, lg: 4, xl: 6},
            pt: {xs: 1.5, md: 4, lg: 4},
            pb: {xs: 1.5, md: 5, lg: 5, xl: 6},
          }}
        >
          <Typography
            fontWeight={{xs: 400, md: 700}}
            fontSize={{xs: 16, md: 20}}
            mx={{xs: 0}}
            mb={{xs: 0.9, md: 1.8}}
            marginTop={{xs: '10px', md: 0}}
          >
            Pending Tests
          </Typography>

          {MdUp && (
            <Grid
              container
              mb={{xs: 1, md: 1.8}}
              p={2}
              borderRadius="8px"
              bgcolor="white"
              height={{xs: 'auto', md: 50}}
              alignItems="center"
            >
              <Grid item md={3}>
                <Typography fontSize={14} fontWeight={700}>
                  Test Name
                </Typography>
              </Grid>
              <Grid item md={7}>
                <Typography fontSize={14} fontWeight={700}>
                  Title
                </Typography>
              </Grid>
              <Grid item md={2} justifyContent="center">
                <Typography fontSize={14} fontWeight={700}>
                  Actions
                </Typography>
              </Grid>
            </Grid>
          )}
          {tests.map((item, index) => (
            <Grid
              container
              key={index}
              px={2}
              py={1.5}
              mb={2.5}
              borderRadius="8px"
              bgcolor="white"
              alignItems="center"
            >
              <Grid item xs={12} sm={9} md={3} alignSelf="flex-start">
                <Typography
                  fontWeight={{xs: 700, sm: 400}}
                  fontSize={{xs: 16}}
                  order={{xs: 1}}
                >
                  {item.testName}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={7}
                pt={{xs: 1.9, md: 0}}
                mt={{sm: 1.6, md: 0}}
                borderTop={{xs: 1, md: 0}}
                borderColor={{xs: '#efefef'}}
                order={{sm: 3, md: 2}}
              >
                <PendingTestsTitle
                  title={item.jobTitle}
                  recruiter={{
                    name: item.recruiterName,
                    organization: item.companyName,
                    profilePicture:
                      item.profileUrl ?? (UserIcon as unknown as string),
                    date: item.createdOn,
                    designation: item.designation,
                  }}
                ></PendingTestsTitle>
              </Grid>
              <Grid
                item
                xs={12}
                sm={3}
                md={2}
                display="flex"
                justifyContent="flex-end"
                alignSelf="flex-start"
                order={{sm: 2, md: 3}}
              >
                <Link
                  key={item.id}
                  href={`/assessment?details=${item.jobId}-${item.recruiterId}-${item.testId}`}
                  passHref
                  legacyBehavior
                >
                  <Button
                    component="a"
                    variant="outlined"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                    }}
                    fullWidth
                  >
                    Complete test
                  </Button>
                </Link>
              </Grid>
            </Grid>
          ))}
        </Box>
      )}
    </>
  );
};

export default PendingAssessments;
